/** @ts-check */

const STORAGE_KEY = 'bv_cart_timer_end_time';

/**
 * Read or create the shared countdown end-timestamp (ms).
 * All `<cart-timer-component>` instances (cart page, drawer, other tabs)
 * share this single value via localStorage so they always tick in sync.
 *
 * @param {number} durationMs - Full countdown duration in milliseconds.
 * @returns {number} End-timestamp (ms since epoch).
 */
function resolveEndTime(durationMs) {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    const ts = Number(raw);
    if (ts > Date.now()) return ts;
  }
  const ts = Date.now() + durationMs;
  localStorage.setItem(STORAGE_KEY, String(ts));
  return ts;
}

class CartTimerComponent extends HTMLElement {
  /** @type {number|null} */
  #intervalId = null;

  /** Full countdown duration in ms (from data-minutes attribute). */
  #durationMs = 30 * 60 * 1000;

  /** @type {HTMLElement|null} */
  #minutesEl = null;

  /** @type {HTMLElement|null} */
  #secondsEl = null;

  /** @type {MutationObserver|null} */
  #observer = null;

  connectedCallback() {
    const mins = Number(this.dataset.minutes) || 30;
    this.#durationMs = mins * 60 * 1000;

    this.#cacheEls();
    resolveEndTime(this.#durationMs);
    this.#render();

    this.#intervalId = window.setInterval(() => this.#render(), 1000);

    // When morphSection re-renders the cart section it replaces child text
    // with the server-rendered defaults ("--"). This observer fires
    // immediately (microtask) and corrects the display before the browser
    // paints, so no flicker is visible.
    this.#observer = new MutationObserver(() => this.#render());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    window.addEventListener('storage', this.#onStorage);
  }

  disconnectedCallback() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
    this.#observer?.disconnect();
    this.#observer = null;
    window.removeEventListener('storage', this.#onStorage);
  }

  /** Sync display when another tab writes a new end-time. */
  #onStorage = (/** @type {StorageEvent} */ e) => {
    if (e.key === STORAGE_KEY) this.#render();
  };

  /** Re-query child refs (needed after morph replaces child nodes). */
  #cacheEls() {
    this.#minutesEl = this.querySelector('[ref="minutes"]');
    this.#secondsEl = this.querySelector('[ref="seconds"]');
  }

  /** Calculate remaining time and update the DOM. */
  #render() {
    if (!this.#minutesEl?.isConnected || !this.#secondsEl?.isConnected) {
      this.#cacheEls();
    }
    if (!this.#minutesEl || !this.#secondsEl) return;

    const raw = localStorage.getItem(STORAGE_KEY);
    let remaining = 0;

    if (raw) {
      remaining = Math.max(0, Math.floor((Number(raw) - Date.now()) / 1000));
    }

    if (remaining <= 0) {
      const ts = Date.now() + this.#durationMs;
      localStorage.setItem(STORAGE_KEY, String(ts));
      remaining = Math.floor(this.#durationMs / 1000);
    }

    const m = String(Math.floor(remaining / 60)).padStart(2, '0');
    const s = String(remaining % 60).padStart(2, '0');

    // Only write when value differs — prevents an infinite observer loop:
    // write → mutation → observer → #render() → same value → no write → stops.
    if (this.#minutesEl.textContent !== m) this.#minutesEl.textContent = m;
    if (this.#secondsEl.textContent !== s) this.#secondsEl.textContent = s;
  }
}

if (!customElements.get('cart-timer-component')) {
  customElements.define('cart-timer-component', CartTimerComponent);
}
